import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Button,
  Card,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { EmptyMessage } from "@components/shared";

function joinId(item) {
  return item.assessment_instrument_item_id || item.id;
}

export default function AssessmentInstrumentStructure({
  instrumentId,
  instrument = null,
  locked = false,
  onChanged = () => {},
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState(instrument?.instrument_items || []);
  const [loading, setLoading] = useState(!instrument?.instrument_items);
  const [busyKey, setBusyKey] = useState(null);

  const [roots, setRoots] = useState([]);
  const [conceptualItems, setConceptualItems] = useState([]);
  const [attachableItems, setAttachableItems] = useState([]);
  const [rootId, setRootId] = useState(null);
  const [conceptualItemId, setConceptualItemId] = useState(null);
  const [instrumentItemId, setInstrumentItemId] = useState(null);
  const [interrogative, setInterrogative] = useState("");
  const [createConceptualItemId, setCreateConceptualItemId] = useState(null);

  useEffect(() => {
    if (instrument?.instrument_items) {
      setItems(instrument.instrument_items || []);
      setLoading(false);
    } else {
      refreshInstrument();
    }
  }, [instrumentId, instrument]);

  useEffect(() => {
    if (!instrument?.variety) return;
    axios
      .post(`/retrieve-conceptual-roots/`, {
        page: 0,
        page_size: 200,
        status: "entire",
        variety: instrument.variety,
      })
      .then(({ data }) => {
        const rows = data.response[0]?.data || [];
        setRoots(
          rows.map((root) => ({
            value: String(root.id),
            label: root.label || root.concept,
          }))
        );
      })
      .catch((err) => toast.error(err));
  }, [instrument?.variety]);

  useEffect(() => {
    if (!rootId) {
      setConceptualItems([]);
      setConceptualItemId(null);
      setCreateConceptualItemId(null);
      return;
    }
    axios
      .post(`/conceptual-roots/${rootId}/retrieve-conceptual-items/`, {
        status: "entire",
        page: 0,
        page_size: 200,
      })
      .then(({ data }) => {
        const rows = data.response[0]?.data || [];
        const options = rows.map((item) => ({
          value: String(item.id),
          label: item.facet_formatted || item.label || item.facet,
        }));
        setConceptualItems(options);
        setConceptualItemId(null);
        setCreateConceptualItemId(null);
        setInstrumentItemId(null);
        setAttachableItems([]);
      })
      .catch((err) => toast.error(err));
  }, [rootId]);

  useEffect(() => {
    if (!rootId || !conceptualItemId) {
      setAttachableItems([]);
      setInstrumentItemId(null);
      return;
    }
    axios
      .post(
        `/conceptual-roots/${rootId}/conceptual-items/${conceptualItemId}/retrieve-instrument-items/`,
        { status: "entire", page: 0, page_size: 200 }
      )
      .then(({ data }) => {
        const rows = data.response[0]?.data || [];
        setAttachableItems(
          rows.map((item) => ({
            value: String(item.id),
            label: item.interrogative || String(item.id),
          }))
        );
        setInstrumentItemId(null);
      })
      .catch((err) => toast.error(err));
  }, [rootId, conceptualItemId]);

  function refreshInstrument() {
    setLoading(true);
    axios
      .get(`/assessment-instruments/${instrumentId}/`)
      .then(({ data }) => {
        const next = data.response[0];
        setItems(next?.instrument_items || []);
        setLoading(false);
        onChanged(next);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  function removeItem(item) {
    const assessmentInstrumentItemId = joinId(item);
    setBusyKey(`remove-${assessmentInstrumentItemId}`);
    axios
      .post(
        `/assessment-instruments/${instrumentId}/items/${assessmentInstrumentItemId}/remove/`
      )
      .then(() => {
        setBusyKey(null);
        toast.success(t("updated"));
        refreshInstrument();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  function moveItem(index, direction) {
    const next = [...items];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    const tmp = next[index];
    next[index] = next[target];
    next[target] = tmp;
    const orderedIds = next.map((item) => joinId(item));
    setBusyKey(`reorder-${index}`);
    axios
      .post(`/assessment-instruments/${instrumentId}/items/reorder/`, {
        assessment_instrument_item_ids: orderedIds,
      })
      .then(() => {
        setBusyKey(null);
        toast.success(t("updated"));
        refreshInstrument();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  function attachItem() {
    if (!instrumentItemId) {
      toast.error(t("validation:required"));
      return;
    }
    setBusyKey("attach");
    axios
      .post(`/assessment-instruments/${instrumentId}/items/attach/`, {
        instrument_item_id: instrumentItemId,
      })
      .then(() => {
        setBusyKey(null);
        toast.success(t("created"));
        setInstrumentItemId(null);
        refreshInstrument();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  function createItem() {
    if (!interrogative.trim() || !createConceptualItemId) {
      toast.error(t("validation:required"));
      return;
    }
    setBusyKey("create");
    axios
      .post(`/assessment-instruments/${instrumentId}/items/`, {
        interrogative: interrogative.trim(),
        conceptual_item_id: createConceptualItemId,
      })
      .then(() => {
        setBusyKey(null);
        toast.success(t("created"));
        setInterrogative("");
        refreshInstrument();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <Stack gap="md">
      {!locked && (
        <>
          <Card>
            <Title order={4} mb="sm">
              Attach existing item
            </Title>
            <Stack gap="sm">
              <Select
                label="Conceptual root"
                data={roots}
                value={rootId}
                onChange={setRootId}
                clearable
                searchable
              />
              <Select
                label="Conceptual item"
                data={conceptualItems}
                value={conceptualItemId}
                onChange={setConceptualItemId}
                disabled={!rootId}
                clearable
                searchable
              />
              <Select
                label="Instrument item"
                data={attachableItems}
                value={instrumentItemId}
                onChange={setInstrumentItemId}
                disabled={!conceptualItemId}
                clearable
                searchable
              />
              <Button
                loading={busyKey === "attach"}
                disabled={!instrumentItemId}
                onClick={attachItem}
              >
                Attach
              </Button>
            </Stack>
          </Card>

          <Card>
            <Title order={4} mb="sm">
              {t("create")} item
            </Title>
            <Stack gap="sm">
              <Select
                label="Conceptual root"
                data={roots}
                value={rootId}
                onChange={setRootId}
                clearable
                searchable
              />
              <Select
                label="Conceptual item"
                data={conceptualItems}
                value={createConceptualItemId}
                onChange={setCreateConceptualItemId}
                disabled={!rootId}
                clearable
                searchable
              />
              <TextInput
                label="Interrogative"
                value={interrogative}
                onChange={(e) => setInterrogative(e.target.value)}
                required
              />
              <Button
                loading={busyKey === "create"}
                disabled={!createConceptualItemId || !interrogative.trim()}
                onClick={createItem}
              >
                {t("create")}
              </Button>
            </Stack>
          </Card>
        </>
      )}

      {locked && (
        <Text size="sm" c="dimmed">
          Structure is locked. Unlock the instrument to attach, create, reorder,
          or remove items.
        </Text>
      )}

      {!items.length && <EmptyMessage />}
      {items.map((item, index) => {
        const assessmentInstrumentItemId = joinId(item);
        return (
          <Card key={assessmentInstrumentItemId}>
            <Group justify="space-between" align="flex-start">
              <div>
                <Text fw={700}>{item.interrogative}</Text>
                <Text size="sm" c="dimmed">
                  {[item.root_label || item.concept, item.facet_formatted || item.facet]
                    .filter(Boolean)
                    .join(" · ")}
                </Text>
              </div>
              {!locked && (
                <Group gap="xs">
                  <Button
                    size="xs"
                    variant="light"
                    disabled={index === 0}
                    loading={busyKey === `reorder-${index}`}
                    onClick={() => moveItem(index, -1)}
                  >
                    Up
                  </Button>
                  <Button
                    size="xs"
                    variant="light"
                    disabled={index === items.length - 1}
                    loading={busyKey === `reorder-${index}`}
                    onClick={() => moveItem(index, 1)}
                  >
                    Down
                  </Button>
                  <Button
                    size="xs"
                    variant="light"
                    color="red"
                    loading={busyKey === `remove-${assessmentInstrumentItemId}`}
                    onClick={() => removeItem(item)}
                  >
                    {t("remove")}
                  </Button>
                </Group>
              )}
            </Group>
          </Card>
        );
      })}
    </Stack>
  );
}
